// booking/components/BookTickets.tsx
'use client'
import React, { useMemo } from 'react'
import cn from 'classnames'
import styles from './index.module.scss'

import ExtrasSelection from './stages/ExtrasSelection'
import QuantitySelection from './stages/QuantitySelection'
import DateAndTime from './stages/DateAndTime'
import AddonsSelection from './stages/AddonsSelection'
import CustomerDetails from './stages/CustomerDetails'
import PaymentProcess from './stages/PaymentProcess'
import ThankYou from './stages/ThankYou'
import Associate from './stages/Associate'

import { useSearchParams } from 'next/navigation'

import { BookingProvider, useBookingActionsCtx, useBookingState } from './state/booking.context'
import { BookingExternalContext } from './state/external.context'
import BookingNav from './components/BookingNav'
import { resolveBookingConfig, stageIdOf, stageNumberOf } from './lib/bookingConfig'
import type { BookingConfig, BookingContextKey, StageId } from './types/bookingConfig'
import { GlobalData } from '.'

const STAGE_COMPONENTS: Record<StageId, React.ReactNode> = {
  voucher: <></>,
  extras: <ExtrasSelection />,
  quantity: <QuantitySelection />,
  datetime: <DateAndTime />,
  addons: <AddonsSelection />,
  customer: <CustomerDetails />,
  payment: <PaymentProcess />,
  thankyou: <ThankYou />,
  associate: <Associate />,
}

export default function BookTicketsPage({ data }: { data: GlobalData }) {
  const searchParams = useSearchParams()

  const flowParam = useMemo(() => {
    const FLOW_ALIASES: Record<string, string> = {
      rir: 'rock-in-rio',
    }
    const paramFlow = searchParams.get('flow') as BookingContextKey | null
    const subdomain = typeof window !== 'undefined' ? window?.location.hostname.split('.')[0] : ''
    const rawFlow = paramFlow ?? subdomain
    console.log('flow param inside')
    console.log(FLOW_ALIASES[rawFlow] ?? rawFlow)
    return FLOW_ALIASES[rawFlow] ?? rawFlow
  }, [searchParams])

  console.log('flow param outside')
  console.log(flowParam)
  const bookingConfig = resolveBookingConfig(flowParam)

  return (
    <BookingExternalContext.Provider value={data ?? null}>
      <BookingProvider bookingConfig={bookingConfig}>
        <BookTicketsInner bookingConfig={bookingConfig} />
      </BookingProvider>
    </BookingExternalContext.Provider>
  )
}

function BookTicketsInner({ bookingConfig }: { bookingConfig: BookingConfig }) {
  const state = useBookingState()
  const actions = useBookingActionsCtx()

  const stage = state.stage ?? stageNumberOf(bookingConfig, bookingConfig.stages[0])

  if (state.error && state.error.level === 'high') {
    return <div className={cn(styles.error)}>{state.error.message}</div>
  }

  const stageId = stageIdOf(bookingConfig, stage)
  const stageComponent = stageId ? STAGE_COMPONENTS[stageId] : null

  return (
    <div className={styles.book}>
      <div className={cn({ 'container mx-auto': true })}>
        <div className={cn(styles.navContainer)}>
          <BookingNav stage={stage} onChange={actions.trackHistory} />
        </div>

        {state.loading && (
          <div className={cn(styles.loading)}>
            <div className={cn(styles.message)}>{state.loading.message}</div>
          </div>
        )}

        {state.error && state.error.scope !== 'local' && (
          <div className={cn(styles.error)}>{state.error.message}</div>
        )}

        {!state.loading && stageComponent}
      </div>
    </div>
  )
}
